import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ImageViewerDialogComponent } from '../image-viewer-dialog/image-viewer-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InspectionPhotoService, PhotoUploadResult } from '../../services/inspection-photo.service';
import { FindingConfigItem } from '../../models/inspection-config.model';
import { InspectionPhoto } from '../../models/inspection-photo.model';

export interface FindingDetailDialogData {
  item: FindingConfigItem;
  findingType: 'PEST' | 'DISEASE' | 'DEFICIENCY';
  tenantId: number;
  reportId: string;
  existingNotes?: string;
  existingPhotos?: InspectionPhoto[];
  voiceNoteUrl?: string;    // playback only — recorded on mobile
  viewOnly?: boolean;
}

export interface FindingDetailDialogResult {
  configItemId: string;
  findingType: 'PEST' | 'DISEASE' | 'DEFICIENCY';
  notes: string;
  photos: InspectionPhoto[];
}

@Component({
  selector: 'app-finding-detail-modal',
  templateUrl: './finding-detail-modal.component.html',
  standalone: false
})
export class FindingDetailModalComponent implements OnInit {

  form: FormGroup;
  photos: InspectionPhoto[] = [];
  uploadingPhoto = false;
  readonly MAX_PHOTOS = 3;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private photoService: InspectionPhotoService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<FindingDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FindingDetailDialogData
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      notes: [{ value: this.data.existingNotes ?? '', disabled: this.data.viewOnly }]
    });
    this.photos = this.data.existingPhotos ? [...this.data.existingPhotos] : [];
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    if (this.photos.length >= this.MAX_PHOTOS) {
      this.snackBar.open(`Maximum ${this.MAX_PHOTOS} photos per entry`, 'Dismiss', { duration: 3000 });
      return;
    }

    const file = input.files[0];
    const section = this.data.findingType.toLowerCase() as 'pest' | 'disease' | 'deficiency';
    this.uploadingPhoto = true;

    this.photoService.uploadPhoto(file, this.data.tenantId, this.data.reportId, section).subscribe({
      next: (result: PhotoUploadResult) => {
        this.photos.push({
          section: this.data.findingType as 'PEST' | 'DISEASE' | 'DEFICIENCY',
          firebaseStoragePath: result.firebaseStoragePath,
          downloadUrl: result.downloadUrl
        });
        this.uploadingPhoto = false;
        input.value = '';
      },
      error: () => {
        this.snackBar.open('Photo upload failed. Please try again.', 'Dismiss', { duration: 3000 });
        this.uploadingPhoto = false;
      }
    });
  }

  removePhoto(index: number): void {
    this.photos.splice(index, 1);
  }

  /** Opens above this modal — Material stacks it, and the finding form stays as it was. */
  openImage(url?: string): void {
    if (!url) return;
    this.dialog.open(ImageViewerDialogComponent, {
      data: { url, caption: this.data.item.name },
      panelClass: 'fi-image-viewer-panel',
      maxWidth: '95vw',
      autoFocus: false
    });
  }

  confirm(): void {
    const result: FindingDetailDialogResult = {
      configItemId: this.data.item.id,
      findingType: this.data.findingType,
      notes: this.form.getRawValue().notes ?? '',
      photos: this.photos
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
